import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { alpha, Box, Checkbox, FormControlLabel, IconButton, Typography, useTheme } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Tree } from 'react-arborist'
import type { NodeApi, NodeRendererProps, TreeApi } from 'react-arborist'
import { NetworkTreeDto } from '../../interfaces/IAffectedCustomers'
import { matchesNetworkTreeNode } from './networkTreeSearch'

type CheckState = 'all' | 'some' | 'none'

type KeyedNode = NetworkTreeDto & { __key: string; childNodes: KeyedNode[] }

interface TreeViewProps {
  data: NetworkTreeDto[]
  onNodesCheck: (ids: string[], isChecked: boolean) => void
  searchTerm?: string
  treeRef?: React.MutableRefObject<TreeApi<NetworkTreeDto> | undefined>
  height?: number
  rowHeight?: number
  indent?: number
}

const DEFAULT_HEIGHT = 600
const DEFAULT_ROW_HEIGHT = 32
const DEFAULT_INDENT = 24

const collectDescendantIds = (node: NetworkTreeDto, acc: string[]): void => {
  acc.push(node.id)
  if (node.childNodes) {
    node.childNodes.forEach((child) => collectDescendantIds(child, acc))
  }
}

const buildCheckStateMap = (data: NetworkTreeDto[]): Map<string, CheckState> => {
  const map = new Map<string, CheckState>()
  const visit = (node: NetworkTreeDto): { hasChecked: boolean; hasUnchecked: boolean } => {
    const isLeaf = !node.childNodes || node.childNodes.length === 0
    if (isLeaf) {
      const checked = !!node.isChecked
      map.set(node.id, checked ? 'all' : 'none')
      return { hasChecked: checked, hasUnchecked: !checked }
    }
    let hasChecked = false
    let hasUnchecked = false
    for (const child of node.childNodes) {
      const childResult = visit(child)
      if (childResult.hasChecked) hasChecked = true
      if (childResult.hasUnchecked) hasUnchecked = true
    }
    let state: CheckState
    if (hasChecked && hasUnchecked) state = 'some'
    else if (hasChecked) state = 'all'
    else state = 'none'
    map.set(node.id, state)
    return { hasChecked, hasUnchecked }
  }
  data.forEach(visit)
  return map
}

const childrenAccessor = (d: KeyedNode) =>
  d.childNodes && d.childNodes.length > 0 ? d.childNodes : null

const idAccessor = (d: KeyedNode) => d.__key

const assignUniqueKeys = (nodes: NetworkTreeDto[]): KeyedNode[] => {
  let counter = 0
  const visit = (input: NetworkTreeDto[]): KeyedNode[] =>
    input.map((node) => ({
      ...node,
      __key: `${node.id}__${counter++}`,
      childNodes: node.childNodes ? visit(node.childNodes) : [],
    }))
  return visit(nodes)
}

const RecursiveTreeView: React.FC<TreeViewProps> = ({
  data,
  onNodesCheck,
  searchTerm,
  treeRef,
  height = DEFAULT_HEIGHT,
  rowHeight = DEFAULT_ROW_HEIGHT,
  indent = DEFAULT_INDENT,
}) => {
  const theme = useTheme()
  const internalTreeRef = useRef<TreeApi<NetworkTreeDto> | undefined>(undefined)
  const effectiveTreeRef = treeRef ?? internalTreeRef
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState<number>(0)

  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width ?? 0
      setWidth((prev) => (prev === next ? prev : next))
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const keyedData = useMemo(() => assignUniqueKeys(data), [data])
  const checkStateMap = useMemo(() => buildCheckStateMap(data), [data])

  const toggleNode = useCallback(
    (node: NodeApi<KeyedNode>) => {
      const currentState = checkStateMap.get(node.data.id) ?? 'none'
      const nextChecked = currentState !== 'all'
      const ids: string[] = []
      collectDescendantIds(node.data, ids)
      onNodesCheck(ids, nextChecked)
    },
    [checkStateMap, onNodesCheck],
  )

  const handleContainerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== ' ') return
      const focused = effectiveTreeRef.current?.focusedNode as NodeApi<KeyedNode> | null | undefined
      if (!focused) return
      e.preventDefault()
      toggleNode(focused)
    },
    [effectiveTreeRef, toggleNode],
  )

  const searchMatch = useCallback(
    (node: NodeApi<KeyedNode>, term: string) => matchesNetworkTreeNode(node.data, term),
    [],
  )

  const renderNode = useCallback(
    ({ node, style }: NodeRendererProps<KeyedNode>) => {
      const data = node.data
      const isInternal = !node.isLeaf
      const modelOrType = data.model ? data.model : data.type
      const circuits = data.circuitNumbers?.length ? data.circuitNumbers.join(', ') : null
      const servicePointId = data.servicePointReference || null
      const isServicePoint = data.type === 'ServicePoint' && (circuits || servicePointId)
      const isEquipmentWithIp = data.type === 'Equipment' && !!data.managementIp

      let label: React.ReactNode
      if (isServicePoint) {
        const primary = [circuits, servicePointId].filter((p) => p !== null).join(' / ')
        label = (
          <>
            {primary} <span style={{ fontWeight: 600 }}>({modelOrType})</span>
          </>
        )
      } else if (isEquipmentWithIp) {
        label = (
          <>
            {data.name}
            {' / '}
            {data.managementIp} <span style={{ fontWeight: 600 }}>({modelOrType})</span>
          </>
        )
      } else {
        label = (
          <>
            {data.name} <span style={{ fontWeight: 600 }}>({modelOrType})</span>
          </>
        )
      }

      const checkState = checkStateMap.get(data.id) ?? 'none'
      const focusedBg = alpha(theme.palette.primary.main, 0.12)
      const hoverBg = alpha(theme.palette.action.hover, 0.08)

      return (
        <Box
          style={style}
          sx={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            height: '100%',
            overflow: 'hidden',
            backgroundColor: node.isFocused ? focusedBg : 'transparent',
            '&:hover': { backgroundColor: node.isFocused ? focusedBg : hoverBg },
          }}
        >
          {Array.from({ length: node.level }).map((_, i) => (
            <Box
              key={i}
              aria-hidden
              sx={{
                position: 'absolute',
                left: i * indent + indent / 2 - 0.75,
                top: 0,
                bottom: 0,
                width: '1.5px',
                backgroundImage: `linear-gradient(to bottom, ${alpha(theme.palette.text.primary, 0.4)} 4px, transparent 4px)`,
                backgroundSize: '1.5px 7px',
                backgroundRepeat: 'repeat-y',
                pointerEvents: 'none',
              }}
            />
          ))}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
              gap: 1,
              position: 'relative',
              zIndex: 1,
              backgroundColor: 'inherit',
            }}
          >
            {isInternal ? (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  node.toggle()
                }}
                sx={{ width: indent, height: indent, p: 0, flexShrink: 0 }}
                aria-label={node.isOpen ? 'Lukk' : 'Åpne'}
              >
                {node.isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>
            ) : (
              <Box sx={{ width: indent, height: indent, flexShrink: 0 }} />
            )}
            <FormControlLabel
              sx={{ minWidth: 0, flex: 1, mr: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={checkState === 'all'}
                  indeterminate={checkState === 'some'}
                  onChange={() => toggleNode(node)}
                  onClick={(e) => e.stopPropagation()}
                />
              }
              label={
                <Typography variant="body2" noWrap title={typeof label === 'string' ? label : undefined}>
                  {label}
                </Typography>
              }
            />
          </Box>
        </Box>
      )
    },
    [checkStateMap, indent, theme, toggleNode],
  )

  return (
    <Box ref={containerRef} sx={{ width: '100%' }} onKeyDown={handleContainerKeyDown}>
      {width > 0 && (
        <Tree<KeyedNode>
          ref={effectiveTreeRef as React.MutableRefObject<TreeApi<KeyedNode> | undefined>}
          data={keyedData}
          idAccessor={idAccessor}
          childrenAccessor={childrenAccessor}
          openByDefault={false}
          disableDrag
          disableDrop
          disableEdit
          disableMultiSelection
          width={width}
          height={height}
          rowHeight={rowHeight}
          indent={indent}
          searchTerm={searchTerm}
          searchMatch={searchMatch}
        >
          {renderNode}
        </Tree>
      )}
    </Box>
  )
}

export default RecursiveTreeView
